import { useEffect, useState } from "react";
import GlowButton from "../components/GlowButton";
import MissingImage from "../components/MissingImage";
import { API_BASE } from "../lib/api";
import { Toaster, toast } from "../components/toaster";
import { dict, getInitialLang, type Lang } from "../lib/i18n";

type Props = { imageId: string };
type Item = { id: number; image_url?: string | null; prompt: string; created_at: string };

export default function PublicImagePage({ imageId }: Props) {
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [lang] = useState<Lang>(() => getInitialLang(null));
  const t = (k: keyof typeof dict["ru"]) => dict[lang][k];

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const r = await fetch(`${API_BASE}/gpt/images/p/${imageId}`);
        if (!r.ok) throw new Error(String(r.status));
        const data = await r.json();
        if (alive) setItem(data);
      } catch (e) {
        if (alive) {
          setItem(null);
          setErr("Изображение не найдено или недоступно.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [imageId]);

  const copyPrompt = async () => {
    if (!item) return;
    try {
      await (navigator.clipboard?.writeText(item.prompt) ?? Promise.reject());
      toast(t("copy_prompt"), "success");
    } catch {
      // фолбэк
      const el = document.createElement("textarea");
      el.value = item.prompt;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
      toast(t("copy_prompt"), "success");
    }
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden text-white">
      <Toaster />
      <div className="pointer-events-none absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-fuchsia-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-[420px] w-[420px] rounded-full bg-emerald-500/20 blur-3xl" />

      <div className="max-w-3xl mx-auto px-4 py-10 md:py-14">
        {/* Заголовок/назад */}
        <div className="mb-4 flex items-center justify-between">
          <a href="#/gallery" className="text-white/70 hover:text-white transition text-sm">{t("back_to_gallery")}</a>
          <span className="text-white/50 text-xs">ID: {imageId}</span>
        </div>

        {/* Карточка */}
        <div className="rounded-3xl border border-white/10 overflow-hidden bg-white/5 backdrop-blur-xl shadow-[0_10px_50px_rgba(0,0,0,.35)]">
          {/* Изображение */}
          <div className="relative">
            {loading ? (
              <div className="aspect-[4/3] animate-pulse bg-white/10" />
            ) : item?.image_url ? (
              <img
                src={item.image_url}
                alt={item.prompt}
                className="w-full h-auto"
              />
            ) : (
              <MissingImage title="Нет изображения" className="w-full aspect-[4/3]" />
            )}

            {/* лёгкая маска снизу, чтобы текст читался */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Контент */}
          <div className="p-4 sm:p-6">
            {/* Промпт */}
            <p className="text-base sm:text-lg text-white/90 leading-relaxed mb-4">
              {loading ? <span className="animate-pulse text-white/50">{t("loading")}</span> : (item?.prompt || "")}
            </p>

            {err && (
              <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white/75 mb-4">
                {err}
              </div>
            )}

            {/* Кнопки действий */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <GlowButton
                as="a"
                href="https://t.me/gpt_5UzBot?startapp=1"
                size="lg"
              >
                {t("try_in_app")}
              </GlowButton>

              <GlowButton onClick={copyPrompt} size="lg">
                {t("copy_prompt")}
              </GlowButton>

              <GlowButton as="a" href="#/public" size="lg">
                {t("open_public")}
              </GlowButton>
            </div>
          </div>
        </div>

        {/* подпись */}
        <div className="mt-6 text-center text-white/40 text-xs">
          {t("share_hint")}
        </div>
      </div>
    </div>
  );
}
